<template>
  <div class="admin-page">
    <h2 class="page-title">购买申诉退款</h2>
    <div class="admin-toolbar">
      <el-input v-model="filters.orderNo" placeholder="订单号" clearable class="filter-input" />
      <el-input v-model="filters.userId" placeholder="用户ID" clearable class="filter-input" />
      <el-select v-model="filters.status" placeholder="状态" clearable class="filter-select" @change="fetchList">
        <el-option label="待审核" value="pending" />
        <el-option label="待补充" value="need_info" />
        <el-option label="已同意" value="approved" />
        <el-option label="已拒绝" value="rejected" />
      </el-select>
      <el-button type="primary" @click="fetchList">查询</el-button>
    </div>

    <el-table :data="items" v-loading="loading" border>
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="orderNo" label="订单号" min-width="160" show-overflow-tooltip />
      <el-table-column prop="productType" label="类型" width="80" />
      <el-table-column label="金额(元)" width="100">
        <template #default="s">{{ centsToYuan(s.row.orderAmountCents) }}</template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template #default="s">{{ appealStatusLabel(s.row.status) }}</template>
      </el-table-column>
      <el-table-column prop="reason" label="申诉理由" min-width="160" show-overflow-tooltip />
      <el-table-column label="分润状态" width="90">
        <template #default="s">{{ commissionStatusLabel(s.row.partnerCommissionStatus) }}</template>
      </el-table-column>
      <el-table-column label="分润(元)" width="90">
        <template #default="s">{{ s.row.partnerCommissionCents ? centsToYuan(s.row.partnerCommissionCents) : '—' }}</template>
      </el-table-column>
      <el-table-column label="凭证" width="80">
        <template #default="s">
          <el-button v-if="s.row.imageUrls?.length" link type="primary" @click="previewImages(s.row)">
            {{ s.row.imageUrls.length }}张
          </el-button>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="提交时间" width="170">
        <template #default="s">{{ formatTime(s.row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="s">
          <el-button
            v-if="s.row.status === 'pending' || s.row.status === 'need_info'"
            link
            type="primary"
            @click="openProcess(s.row)"
          >
            处理
          </el-button>
          <span v-else>—</span>
        </template>
      </el-table-column>
    </el-table>

    <AdminPagination
      v-model:current-page="page"
      v-model:page-size="pageSize"
      :total="total"
      @size-change="fetchList"
      @current-change="fetchList"
    />

    <el-dialog v-model="processVisible" title="处理申诉" width="520px" destroy-on-close>
      <p v-if="currentRow"><strong>订单：</strong>{{ currentRow.orderNo }}</p>
      <p v-if="currentRow?.reason"><strong>用户理由：</strong>{{ currentRow.reason }}</p>
      <p v-if="currentRow?.adminReply && currentRow.status === 'need_info'">
        <strong>上次回复：</strong>{{ currentRow.adminReply }}
      </p>
      <el-form label-width="90px" class="process-form">
        <el-form-item label="操作">
          <el-radio-group v-model="processForm.action">
            <el-radio label="approve">同意退款</el-radio>
            <el-radio label="reject">拒绝</el-radio>
            <el-radio label="need_info">要求补充</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="回复说明">
          <el-input
            v-model="processForm.adminReply"
            type="textarea"
            :rows="3"
            placeholder="拒绝或要求补充时请填写说明"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="processVisible = false">取消</el-button>
        <el-button type="primary" :loading="processing" @click="submitProcess">确认</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="previewVisible" title="申诉凭证" width="640px">
      <div class="preview-grid">
        <el-image
          v-for="(url, i) in previewUrls"
          :key="i"
          :src="url"
          :preview-src-list="previewUrls"
          fit="cover"
          class="preview-img"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AdminPagination from '@/components/AdminPagination.vue'
import { photoPathToUrl } from '@/utils/photoUpload'
import {
  appealStatusLabel,
  fetchAdminPurchaseRefundAppeals,
  processAdminPurchaseRefundAppeal
} from '@/utils/purchaseRefund'
import { commissionStatusLabel } from '@/utils/referralCoupon'

export default {
  name: 'AdminPurchaseRefundAppeals',
  components: { AdminPagination },
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { status: '', orderNo: '', userId: '' },
      processVisible: false,
      processing: false,
      currentRow: null,
      processForm: { action: 'approve', adminReply: '' },
      previewVisible: false,
      previewUrls: []
    }
  },
  created() {
    const status = this.$route.query.status
    if (typeof status === 'string' && status) {
      this.filters.status = status
    }
    this.fetchList()
  },
  methods: {
    appealStatusLabel,
    commissionStatusLabel,
    centsToYuan(cents) {
      return (Number(cents || 0) / 100).toFixed(2)
    },
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    async fetchList() {
      this.loading = true
      try {
        const userId = parseInt(this.filters.userId, 10) || 0
        const data = await fetchAdminPurchaseRefundAppeals({
          page: this.page,
          pageSize: this.pageSize,
          status: this.filters.status,
          orderNo: this.filters.orderNo.trim(),
          userId
        })
        this.items = data.items || []
        this.total = data.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    previewImages(row) {
      this.previewUrls = (row.imageUrls || []).map(photoPathToUrl)
      this.previewVisible = true
    },
    openProcess(row) {
      this.currentRow = row
      this.processForm = { action: 'approve', adminReply: '' }
      this.processVisible = true
    },
    async submitProcess() {
      if (!this.currentRow) return
      if ((this.processForm.action === 'reject' || this.processForm.action === 'need_info') && !this.processForm.adminReply.trim()) {
        this.$message.warning('请填写回复说明')
        return
      }
      this.processing = true
      try {
        await processAdminPurchaseRefundAppeal({
          appealId: this.currentRow.id,
          action: this.processForm.action,
          adminReply: this.processForm.adminReply.trim()
        })
        this.$message.success('处理成功')
        this.processVisible = false
        this.fetchList()
      } catch (e) {
        this.$message.error(e.message || '处理失败')
      } finally {
        this.processing = false
      }
    }
  }
}
</script>

<style scoped>
.page-title { margin: 0 0 16px; font-size: 1.25rem; }
.admin-toolbar { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; align-items: center; }
.filter-input { width: 160px; }
.filter-select { width: 140px; }
.process-form { margin-top: 12px; }
.preview-grid { display: flex; flex-wrap: wrap; gap: 12px; }
.preview-img { width: 120px; height: 120px; border-radius: 6px; }
</style>
